import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LogOut, Users, UserCheck, UserX, RefreshCw, FileSpreadsheet, ExternalLink, CheckCircle2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "wedding_admin_token";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border border-rose-antique/40 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-rose-antique transition-colors";

  return (
    <div className="min-h-screen bg-wedding-pattern flex items-center justify-center p-6 font-sans">
      <form onSubmit={submit} className="w-full max-w-sm bg-cream-bg p-8 rounded-3xl border border-rose-antique/30 shadow-xl space-y-6" data-testid="admin-login-form">
        <div className="text-center space-y-2">
          <h1 className="font-serif text-3xl text-dark-charcoal">Area Admin</h1>
          <p className="text-xs text-dark-charcoal/60 uppercase tracking-wider">Dounya &amp; Yassine</p>
        </div>
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="admin-email-input" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="admin-password-input" />
        {error && <p className="text-red-600 text-sm text-center" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading} className="w-full bg-dark-charcoal hover:bg-rose-antique text-cream-bg py-3.5 rounded-xl text-xs tracking-widest uppercase transition-colors disabled:opacity-60" data-testid="admin-login-btn">
          {loading ? "Accesso..." : "Accedi"}
        </button>
      </form>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-cream-bg border border-rose-antique/30 rounded-2xl p-5 flex items-center gap-4 shadow-sm" data-testid={testid}>
    <div className="w-11 h-11 rounded-full bg-rose-blush/40 flex items-center justify-center text-rose-antique"><Icon className="w-5 h-5" /></div>
    <div>
      <p className="font-serif text-2xl text-dark-charcoal">{value}</p>
      <p className="text-[11px] uppercase tracking-wider text-dark-charcoal/60">{label}</p>
    </div>
  </div>
);

const Dashboard = ({ token, onLogout }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sheets, setSheets] = useState(null);

  const loadSheets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/sheets/status`, { headers: { Authorization: `Bearer ${token}` } });
      setSheets(res.data);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("Errore nel recupero stato Google Fogli:", e);
    }
  }, [token]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/rsvps`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else setError("Errore nel caricamento dei dati.");
    }
  }, [token, onLogout]);

  useEffect(() => { load(); loadSheets(); }, [load, loadSheets]);

  const connectSheets = () => {
    window.location.href = `${API}/oauth/sheets/login?token=${encodeURIComponent(token)}`;
  };

  const deleteRsvp = async (id) => {
    if (!window.confirm("Vuoi eliminare definitivamente questa risposta?")) return;
    try {
      await axios.delete(`${API}/admin/rsvps/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      load();
    } catch {
      setError("Impossibile eliminare la risposta.");
    }
  };

  const syncSheets = async () => {
    try {
      await axios.post(`${API}/admin/sheets/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      window.alert("Foglio Google sincronizzato con tutte le risposte attuali.");
    } catch (e) {
      window.alert(e.response?.data?.detail || "Sincronizzazione fallita.");
    }
  };

  const s = data?.stats;

  return (
    <div className="min-h-screen bg-wedding-pattern font-sans p-6 md:p-10">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl text-dark-charcoal">Risposte RSVP</h1>
            <p className="text-xs text-dark-charcoal/60 uppercase tracking-wider mt-1">Dounya &amp; Yassine — 17 Ottobre 2026</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="w-10 h-10 rounded-xl border border-rose-antique/40 bg-cream-bg flex items-center justify-center hover:bg-rose-blush/30 transition-colors" data-testid="admin-refresh-btn" title="Aggiorna"><RefreshCw className="w-4 h-4 text-dark-charcoal" /></button>
            <button onClick={onLogout} className="inline-flex items-center gap-2 px-4 rounded-xl border border-rose-antique/40 bg-cream-bg text-xs uppercase tracking-wider hover:bg-rose-blush/30 transition-colors" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" /> Esci</button>
          </div>
        </div>

        {s && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard icon={Users} label="Risposte totali" value={s.total_responses} testid="stat-total" />
            <StatCard icon={UserCheck} label="Presenti" value={s.attending} testid="stat-attending" />
            <StatCard icon={UserX} label="Assenti" value={s.not_attending} testid="stat-absent" />
            <StatCard icon={Users} label="Ospiti totali" value={s.total_guests} testid="stat-guests" />
            <StatCard icon={Users} label="Bambini totali" value={s.total_children ?? 0} testid="stat-children" />
          </div>
        )}

        {/* Google Sheets */}
        <div className="bg-cream-bg border border-rose-antique/30 rounded-2xl p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 shadow-sm" data-testid="sheets-card">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-rose-blush/40 flex items-center justify-center text-rose-antique"><FileSpreadsheet className="w-5 h-5" /></div>
            <div>
              <p className="font-serif text-lg text-dark-charcoal">Google Fogli</p>
              <p className="text-xs text-dark-charcoal/60">{sheets?.connected ? "Collegato — le nuove risposte vengono aggiunte automaticamente." : "Non collegato. Collega per salvare le risposte anche su Google Fogli."}</p>
            </div>
          </div>
          {sheets?.connected ? (
            <div className="flex flex-wrap gap-2">
              <button onClick={connectSheets} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-dark-charcoal text-cream-bg text-xs uppercase tracking-wider hover:bg-rose-antique transition-colors" data-testid="reconnect-sheets-btn">
                <FileSpreadsheet className="w-4 h-4" /> Ricollega
              </button>
              <button onClick={syncSheets} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-rose-antique/40 bg-cream-bg text-xs uppercase tracking-wider hover:bg-rose-blush/30 transition-colors" data-testid="sync-sheets-btn">
                <RefreshCw className="w-4 h-4" /> Sincronizza
              </button>
              <a href={sheets.spreadsheet_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-rose-antique/40 bg-cream-bg text-xs uppercase tracking-wider hover:bg-rose-blush/30 transition-colors" data-testid="open-sheet-btn">
                <ExternalLink className="w-4 h-4" /> Apri il foglio
              </a>
            </div>
          ) : (
            <button onClick={connectSheets} className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-dark-charcoal text-cream-bg text-xs uppercase tracking-wider hover:bg-rose-antique transition-colors" data-testid="connect-sheets-btn">
              <FileSpreadsheet className="w-4 h-4" /> Collega Google Fogli
            </button>
          )}
        </div>

        {new URLSearchParams(window.location.search).get("sheets") === "connected" && (
          <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm" data-testid="sheets-connected-banner">
            <CheckCircle2 className="w-4 h-4" /> Google Fogli collegato con successo!
          </div>
        )}

        {new URLSearchParams(window.location.search).get("sheets") === "error" && (
          <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm" data-testid="sheets-error-banner">
            Collegamento a Google Fogli non riuscito. Riprova, assicurandoti di accedere con l'account Gmail autorizzato.
          </div>
        )}

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <div className="bg-cream-bg border border-rose-antique/30 rounded-2xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rsvp-table">
              <thead className="bg-rose-blush/30 text-dark-charcoal">
                <tr className="text-left uppercase text-[11px] tracking-wider">
                  <th className="p-4">Nome</th>
                  <th className="p-4">Presenza</th>
                  <th className="p-4">Ospiti</th>
                  <th className="p-4">Nomi ospiti</th>
                  <th className="p-4">Bambini</th>
                  <th className="p-4">Data</th>
                  <th className="p-4 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {data?.rsvps?.length ? (
                  data.rsvps.map((r) => (
                    <tr key={r.id} className="border-t border-rose-antique/15" data-testid="rsvp-row">
                      <td className="p-4 font-medium text-dark-charcoal">{r.full_name}</td>
                      <td className="p-4">
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${r.attending ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {r.attending ? "Sì" : "No"}
                        </span>
                      </td>
                      <td className="p-4 text-dark-charcoal/80">{r.guests_count}</td>
                      <td className="p-4 text-dark-charcoal/80 whitespace-pre-line">{r.guests_names || "—"}</td>
                      <td className="p-4 text-dark-charcoal/80">{r.children_info || "—"}</td>
                      <td className="p-4 text-dark-charcoal/60 text-xs">{new Date(r.created_at).toLocaleDateString("it-IT")}</td>
                      <td className="p-4 text-right">
                        <button onClick={() => deleteRsvp(r.id)} className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-rose-antique/30 text-red-600 hover:bg-red-50 transition-colors" data-testid="delete-rsvp-btn" title="Elimina">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={7} className="p-8 text-center text-dark-charcoal/50">Nessuna risposta ancora.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  if (!token) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
